import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Check, ChevronDown, LogOut, Menu, Moon, Search, Settings, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useTheme } from '../../hooks/useTheme';
import { NotificationBell } from './NotificationBell';
import { CommandPalette } from './CommandPalette';
import { NewMenu } from './NewMenu';
import { EntitySelector } from '../portal/EntitySelector';
import api from '../../lib/api';
import { lettersAPI } from '../../lib/lettersAPI';

const initials = (name = '') => name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const SearchTrigger = ({ onOpen }) => {
  const mac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  return (
    <button type="button" onClick={onOpen} data-testid="global-search-trigger"
      className="flex h-9 w-full max-w-[520px] items-center gap-2 rounded-md border border-border bg-background px-3 text-left text-sm text-muted-foreground transition-colors ease-ui hover:border-primary/40">
      <Search className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">Search clients, entities, documents, tasks, BAS, tax returns…</span>
      <kbd className="hidden rounded border border-border bg-muted px-1.5 text-[11px] font-medium sm:inline">{mac ? '⌘' : 'Ctrl'} K</kbd>
    </button>
  );
};

const OrgSwitcher = ({ organizations, activeOrganization, onSwitchOrg }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" className="h-9 gap-2 px-2" data-testid="org-switcher">
        <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <span className="hidden max-w-[160px] truncate text-sm lg:inline">{activeOrganization?.name || 'Select Org'}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-64">
      <DropdownMenuLabel className="text-xs text-muted-foreground">Switch organisation</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {organizations.map((org) => (
        <DropdownMenuItem key={org.id} onClick={() => onSwitchOrg(org.id)} data-testid={`org-option-${org.id}`}>
          <span className="flex-1 truncate">{org.name}</span>{org.id === activeOrganization?.id && <Check className="h-4 w-4 text-primary" />}
        </DropdownMenuItem>))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Header = ({ user, role, organizations, activeOrganization, onSwitchOrg, onLogout, enabledFeatures }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isStaff = role && role !== 'client';
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [logo, setLogo] = useState('');
  useEffect(() => {
    if (role !== 'client' || !activeOrganization?.id) return undefined;
    let url = '';
    lettersAPI.brandingLogo().then((b) => { url = URL.createObjectURL(b); setLogo(url); }).catch(() => setLogo(''));
    return () => url && URL.revokeObjectURL(url);
  }, [role, activeOrganization?.id]);
  useEffect(() => {
    if (!isStaff) return undefined;
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isStaff]);
  const signOutEverywhere = async () => {
    try { const { data } = await api.post('/api/auth/sessions/revoke-all'); toast.success(`Signed out of ${data.sessions_revoked} session(s)`); } finally { onLogout(); }
  };

  return (
    <header className="sticky top-0 z-50 h-14 shrink-0 border-b border-border bg-card/85 backdrop-blur-md" data-testid="app-header">
      <div className="flex h-full items-center gap-3 px-3 sm:px-5">
        <Button variant="ghost" size="icon" className="h-9 w-9 md:hidden" aria-label="Open menu" onClick={() => window.dispatchEvent(new Event('kv-mobile-nav'))} data-testid="mobile-nav-toggle"><Menu className="h-5 w-5" /></Button>
        {logo && <img src={logo} alt={`${activeOrganization?.name || 'Practice'} logo`} className="h-8 max-w-[120px] object-contain" data-testid="header-practice-logo" />}
        {role === 'client' && <span className="truncate text-sm font-semibold md:hidden">{activeOrganization?.name || 'Practice Suite'}</span>}
        <div className="min-w-0 flex-1">{isStaff && <SearchTrigger onOpen={() => setPaletteOpen(true)} />}</div>
        <div className="flex items-center gap-1 sm:gap-2">
          {role === 'client' && <div className="max-w-[180px] min-w-0 sm:max-w-none"><EntitySelector /></div>}
          {isStaff && role !== 'read_only' && <NewMenu enabledFeatures={enabledFeatures} />}
          <NotificationBell />
          {organizations?.length > 0 && <div className="hidden sm:block"><OrgSwitcher organizations={organizations} activeOrganization={activeOrganization} onSwitchOrg={onSwitchOrg} /></div>}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 gap-2 pl-1 pr-2" data-testid="user-menu">
                  <Avatar className="h-7 w-7"><AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">{initials(user.name)}</AvatarFallback></Avatar>
                  <span className="hidden text-sm xl:inline">{user.name}</span><ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel><p className="font-medium">{user.name}</p><p className="mt-0.5 text-xs font-normal text-muted-foreground">{user.email}</p></DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleTheme(); }} data-testid="theme-toggle">
                  {theme === 'dark' ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}{theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(isStaff ? '/settings' : '/my-settings')} data-testid="settings-menu-item"><Settings className="mr-2 h-4 w-4" />Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOutEverywhere} data-testid="signout-all-menu-item"><LogOut className="mr-2 h-4 w-4" />Sign out all sessions</DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout} data-testid="logout-menu-item"><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>)}
        </div>
      </div>
      {isStaff && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
    </header>
  );
};
